import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import mappingService from '../services/mappingService';

// Get all mappings with optional filters
export const getMappings = createAsyncThunk(
  'mappings/getMappings',
  async (filters = {}, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Calling mappingService.getAll() with filters:', filters);
      }
      const mappings = await mappingService.getAll(filters);
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Received mappings from service:', mappings);
      }
      return mappings;
    } catch (error) {
      console.error('mappingsSlice: Error fetching mappings:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get a single mapping
export const getMapping = createAsyncThunk(
  'mappings/getMapping',
  async (id, { rejectWithValue }) => {
    try {
      return await mappingService.getById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Create a new mapping
export const createMapping = createAsyncThunk(
  'mappings/createMapping',
  async (mappingData, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Creating mapping with data:', mappingData);
      }
      const result = await mappingService.create(mappingData);
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Mapping created successfully:', result);
      }
      return result;
    } catch (error) {
      console.error('mappingsSlice: Error creating mapping:', error);

      // Extract the error message
      let errorMessage = error.message;

      // If there's a response with data
      if (error.response?.data) {
        if (process.env.NODE_ENV === 'development') {
          console.error('mappingsSlice: Error response data:', error.response.data);
        }

        // Handle different error data formats
        if (typeof error.response.data === 'object') {
          // If it's an object, try to extract the message or error field
          errorMessage = error.response.data.message || error.response.data.error || JSON.stringify(error.response.data);
        } else if (typeof error.response.data === 'string') {
          // If it's a string, use it directly
          errorMessage = error.response.data;
        }
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Update a mapping
export const updateMapping = createAsyncThunk(
  'mappings/updateMapping',
  async ({ id, mappingData }, { rejectWithValue }) => {
    try {
      return await mappingService.update(id, mappingData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete a mapping
export const deleteMapping = createAsyncThunk(
  'mappings/deleteMapping',
  async (id, { rejectWithValue }) => {
    try {
      await mappingService.remove(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Validate a mapping with sample data
export const validateMapping = createAsyncThunk(
  'mappings/validateMapping',
  async ({ mappingId, inputData }, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Validating mapping:', mappingId, 'with data:', inputData);
      }
      const result = await mappingService.validateMapping(mappingId, inputData);
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Validation result:', result);
      }
      return result;
    } catch (error) {
      console.error('mappingsSlice: Error validating mapping:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get schema usage
export const getSchemaUsage = createAsyncThunk(
  'mappings/getSchemaUsage',
  async (schemaId, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Getting schema usage for:', schemaId);
      }
      const result = await mappingService.getSchemaUsage(schemaId);
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Schema usage result:', result);
      }
      return result;
    } catch (error) {
      console.error('mappingsSlice: Error getting schema usage:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get mappings by schema
export const getMappingsBySchema = createAsyncThunk(
  'mappings/getMappingsBySchema',
  async (schemaId, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Getting mappings for schema:', schemaId);
      }
      const result = await mappingService.getMappingsBySchema(schemaId);
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingsSlice: Mappings by schema result:', result);
      }
      return result;
    } catch (error) {
      console.error('mappingsSlice: Error getting mappings by schema:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const mappingsSlice = createSlice({
  name: 'mappings',
  initialState: {
    mappings: [],
    currentMapping: null,
    loading: false,
    error: null,
    success: false,
    validationResult: null,
    validationLoading: false,
    schemaUsage: null,
    schemaUsageLoading: false,
    filters: {
      mapping_type: 'all',
      schema_id: '',
      search: '',
      page: 1,
      page_size: 50
    }
  },
  reducers: {
    setCurrentMapping: (state, action) => {
      state.currentMapping = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        mapping_type: 'all',
        schema_id: '',
        search: '',
        page: 1,
        page_size: 50
      };
    },
    clearValidationResult: (state) => {
      state.validationResult = null;
    },
    clearSchemaUsage: (state) => {
      state.schemaUsage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all mappings
      .addCase(getMappings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMappings.fulfilled, (state, action) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('mappingsSlice: getMappings fulfilled with payload:', action.payload);
        }
        state.loading = false;
        state.mappings = action.payload;
        if (process.env.NODE_ENV === 'development') {
          console.log('mappingsSlice: Updated mappings state:', state.mappings);
        }
      })
      .addCase(getMappings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch mappings';
      })

      // Get a single mapping
      .addCase(getMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMapping.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMapping = action.payload;
      })
      .addCase(getMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch mapping';
      })

      // Create a new mapping
      .addCase(createMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createMapping.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.mappings.push(action.payload);
      })
      .addCase(createMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create mapping';
      })

      // Update a mapping
      .addCase(updateMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateMapping.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Find the index of the mapping to update
        const index = state.mappings.findIndex(
          (mapping) => mapping.id === action.payload.id || mapping.mapping_id === action.payload.mapping_id
        );

        // If found, update it
        if (index !== -1) {
          state.mappings[index] = action.payload;
        }

        // Also update currentMapping if it's the same mapping
        if (
          state.currentMapping &&
          (state.currentMapping.id === action.payload.id ||
           state.currentMapping.mapping_id === action.payload.mapping_id)
        ) {
          state.currentMapping = action.payload;
        }
      })
      .addCase(updateMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update mapping';
      })

      // Delete a mapping
      .addCase(deleteMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteMapping.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Remove the deleted mapping from the array
        state.mappings = state.mappings.filter(
          (mapping) => mapping.id !== action.payload && mapping.mapping_id !== action.payload
        );

        // Clear currentMapping if it's the deleted mapping
        if (
          state.currentMapping &&
          (state.currentMapping.id === action.payload ||
           state.currentMapping.mapping_id === action.payload)
        ) {
          state.currentMapping = null;
        }
      })
      .addCase(deleteMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete mapping';
      })

      // Validate mapping
      .addCase(validateMapping.pending, (state) => {
        state.validationLoading = true;
        state.error = null;
        state.validationResult = null;
      })
      .addCase(validateMapping.fulfilled, (state, action) => {
        state.validationLoading = false;
        state.validationResult = action.payload;
      })
      .addCase(validateMapping.rejected, (state, action) => {
        state.validationLoading = false;
        state.error = action.payload || 'Failed to validate mapping';
      })

      // Get schema usage
      .addCase(getSchemaUsage.pending, (state) => {
        state.schemaUsageLoading = true;
        state.error = null;
        state.schemaUsage = null;
      })
      .addCase(getSchemaUsage.fulfilled, (state, action) => {
        state.schemaUsageLoading = false;
        state.schemaUsage = action.payload;
      })
      .addCase(getSchemaUsage.rejected, (state, action) => {
        state.schemaUsageLoading = false;
        state.error = action.payload || 'Failed to get schema usage';
      })

      // Get mappings by schema
      .addCase(getMappingsBySchema.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMappingsBySchema.fulfilled, (state, action) => {
        state.loading = false;
        // This could be used to populate a filtered view
        // For now, we'll just store it in mappings
        state.mappings = action.payload;
      })
      .addCase(getMappingsBySchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get mappings by schema';
      });
  },
});

export const {
  setCurrentMapping,
  clearError,
  clearSuccess,
  setFilters,
  clearFilters,
  clearValidationResult,
  clearSchemaUsage
} = mappingsSlice.actions;

export default mappingsSlice.reducer;
